/* eslint-disable @typescript-eslint/dot-notation */
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NbDialogService, NbToggleModule, NbTooltipModule, NbAccordionModule, NbCardModule } from '@nebular/theme';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { Xliff } from 'src/app/models/drb-models/xliff';
import { ACTIONS, DRBTYPE, MODE } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { ENVIRONMENT } from 'src/environments/environment';
import { DeleteDrbComponent } from '../delete-drb/delete-drb.component';
import { DrbState, LoadDrb, LoadDrbByVersionSuccess, SaveDrbState } from '../redux';
import { EditXliffComponent } from './edit-xliff/edit-xliff.component';
import { ViewXliffComponent } from './view-xliff/view-xliff.component';
import { NgIf } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'app-drb-xliff',
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './drb-xliff.component.html',
    styleUrls: ['./drb-xliff.component.css'],
    imports: [NgIf, NbToggleModule, NbTooltipModule, NbAccordionModule, SharedModule, RouterOutlet, NbCardModule]
})
export class DrbXliffComponent implements OnInit, OnDestroy {
  public drb$: Observable<any> = inject(Store).select(DrbState.drb);
  public viewDrb$: Observable<any[]> = inject(Store).select(DrbState.view_drb);
  public xliff$: Observable<any[]> = inject(Store).select(DrbState.xliff);
  public viewXliff$: Observable<any[]> = inject(Store).select(DrbState.view_xliff);
  public translating$: Observable<boolean> = inject(Store).select(DrbState.translating);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  public tableData;
  public showActions = [];
  public mode = MODE.VIEW;
  public metaData;
  public username;
  public description;
  public type = DRBTYPE.STRING;
  public translating;
  public bundle_name: string;

  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store,
    private dialogService: NbDialogService,
    public utility: UtilityService,
    private restrictionService: OktaRestriction
  ) {}

  public ngOnInit() {
    this.bundle_name = this.router.url.split('/')[2];
    this.translating$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.translating = data));
    if (this.router.url.endsWith('create/strings')) {
      this.description = $localize`View, add and edit string data`;
      this.drb$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.metaData = data));
      this.xliff$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
      this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
      this.checkOwnerOrContributor();
    } else if (this.router.url.endsWith('view/strings')) {
      this.description = $localize`View string data`;
      this.viewDrb$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.metaData = data));
      this.viewXliff$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
      this.actions$.pipe(ofActionCompleted(LoadDrbByVersionSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.viewXliff$.pipe(takeUntil(this.unsubscribe)).subscribe((loadedData) => (this.tableData = loadedData));
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.dialogService
        .open(NgxGenericModalComponent, {
          context: {
            title: $localize`Confirm Refresh`,
            message: $localize`Are you sure that you want to refresh draft version?`
          }
        })
        .onClose.subscribe((data) => {
          if (data) {
            this.tableData = undefined;
            this.store.dispatch(new LoadDrb(this.bundle_name));
          }
        });
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditXliffComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          xliff: new Xliff(),
          bundle_name: this.bundle_name
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const xliff: Xliff = $event.data;
      this.dialogService.open(ViewXliffComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          xliff
        }
      });
    }
    if ($event.action === ACTIONS.EDIT) {
      const xliff: Xliff = $event.data;
      this.dialogService.open(EditXliffComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          xliff: JSON.parse(JSON.stringify(xliff)),
          bundle_name: this.bundle_name
        }
      });
    }
    if ($event.action === ACTIONS.DELETE) {
      const xliff: Xliff = $event.data;
      this.dialogService.open(DeleteDrbComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          item: JSON.parse(JSON.stringify(xliff)),
          type: this.type,
          bundle_name: this.bundle_name
        }
      });
    }
  }

  public checkOwnerOrContributor() {
    if (this.restrictionService.manager_permission) {
      if (this.username === this.metaData.owner || this.metaData.contributors.includes(this.username)) {
        this.mode = MODE.EDIT;
        this.showActions = [ACTIONS.CREATE, ACTIONS.EDIT];
        if (!ENVIRONMENT.isProduction) {
          this.showActions.push(ACTIONS.DELETE);
        }
      } else {
        if (this.translating) {
          this.mode = MODE.EDIT;
        } else {
          this.mode = MODE.VIEW;
        }
      }
    }
  }

  public optForTranslation() {
    const localDrb = JSON.parse(JSON.stringify(this.metaData));

    localDrb.last_updated_by = this.username;
    localDrb.last_updated_ts = Math.floor(Date.now() / 1000);
    localDrb.translate = !this.metaData.translate;
    localDrb.bundle_name = this.bundle_name;

    this.store.dispatch(new SaveDrbState(localDrb));
  }

  public getNextMonday(date = new Date()) {
    const dateCopy = new Date(date.getTime());
    let nextMonday;
    if (dateCopy.getHours() < 16) {
      nextMonday = new Date(dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + 1) % 7)));
    } else {
      nextMonday = new Date(dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + 1) % 7 || 7)));
    }

    return nextMonday.getFullYear() + '/' + (nextMonday.getMonth() + 1) + '/' + nextMonday.getDate();
  }

  public getTwoWeeksFromNow(timestamp: number) {
    const date = timestamp + 86400 * 14;

    return this.utility.convertUnixTime_global(date);
  }
}
